import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getAuth, signInWithPopup, signInWithRedirect, getRedirectResult, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

const auth = getAuth();

// ============================================
// RESPONSIVE HOOK
// ============================================
const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    isTablet: windowSize.width > 768,
    isMobile: windowSize.width <= 768,
    width: windowSize.width,
  };
};

// ============================================
// ADMIN EMAIL LIST (Centralized)
// ============================================
const ADMIN_EMAILS = [
  'franklyn@preptune.com',
  'kogolof1@montclair.edu',
  'support@preptune.com'
];

const AppIntro = () => {
  const responsive = useResponsive();
  const [isLogin, setIsLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [loadedImages, setLoadedImages] = useState([]);
  const [authUser, setAuthUser] = useState(null);
  const [activeTab, setActiveTab] = useState('login');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const containerRef = useRef(null);

  const imageCards = useMemo(() => [
    { image: "/image/intro16.png", rotation: -12 },
    { image: "/image/intro2.png", rotation: -6 },
    { image: "/image/intro18.png", rotation: 0 },
    { image: "/image/intro15.png", rotation: 6 },
    { image: "/image/intro4.png", rotation: 12 }
  ], []);

  useEffect(() => {
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute('content', '#000000');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = '#000000';
      document.head.appendChild(meta);
    }

    if (window.StatusBar) {
      window.StatusBar.backgroundColorByHexString('#000000');
      window.StatusBar.styleDefault();
    }

    return () => {
      if (metaTheme) {
        metaTheme.setAttribute('content', '#ffffff');
      }
    };
  }, []);

  useEffect(() => {
    const indices = [0, 1, 2, 3, 4].sort(() => Math.random() - 0.5);
    const timeouts = [];
    
    indices.forEach((index, i) => {
      const timeout = setTimeout(() => {
        setLoadedImages(prev => [...prev, index]);
      }, i * 150);
      timeouts.push(timeout);
    });

    return () => timeouts.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const checkRedirectResult = async () => {
      if (!Capacitor.isNativePlatform()) {
        try {
          const result = await getRedirectResult(auth);
          if (result) {
            console.log('Redirect sign-in successful:', result.user.email);
          }
        } catch (err) {
          console.error('Redirect result error:', err);
          if (err.code === 'auth/popup-closed-by-user' || err.message?.includes('cancelled')) {
            setError('Sign-in cancelled.');
          } else {
            setError('Sign-in failed. Please try again.');
          }
        }
      }
    };
    
    checkRedirectResult();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('USER SIGNED IN SUCCESSFULLY!');
        console.log('Email:', user.email);
        console.log('User ID:', user.uid);
        setAuthUser(user);
        setLoading(false);
      } else {
        console.log('No user signed in');
        setAuthUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    // Don't track movement - just detect swipe at end
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const currentX = e.changedTouches[0].clientX;
    const diff = currentX - dragStart;
    const threshold = 50;
    
    if (diff > threshold) {
      setActiveTab('login');
    } else if (diff < -threshold) {
      setActiveTab('about');
    }
  }, [isDragging, dragStart]);

  // ============================================
  // VALIDATION HELPERS
  // ============================================
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  const isValidPassword = (password) => {
    const minLength = password.length >= 8;
    const maxLength = password.length <= 128;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    return minLength && maxLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
  };

  const getPasswordErrors = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('at least 8 characters');
    if (password.length > 128) errors.push('maximum 128 characters');
    if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('one number');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push('one special character (!@#$%^&*...)');
    return errors;
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++;
    
    if (strength <= 2) return { level: 'weak', color: '#ef4444' };
    if (strength <= 4) return { level: 'medium', color: '#f59e0b' };
    return { level: 'strong', color: '#10b981' };
  };

  // ============================================
  // GOOGLE SIGN IN
  // ============================================
  const handleGoogleSignIn = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const isNative = Capacitor.isNativePlatform();
      
      if (isNative) {
        console.log('Starting native Google sign-in...');
        try {
          const result = await FirebaseAuthentication.signInWithGoogle();
          console.log('Native sign-in result:', result);
          
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const currentUser = auth.currentUser;
          
          if (currentUser) {
            console.log('Firebase user confirmed:', currentUser.email);
          } else {
            console.error('User signed in but Firebase user not found');
            setError('Sign-in incomplete. Please try again.');
            setLoading(false);
          }
        } catch (nativeErr) {
          console.error('Native sign-in error:', nativeErr);
          
          if (nativeErr.message?.includes('cancelled') || nativeErr.message?.includes('canceled') || nativeErr.code === '10') {
            setError('Sign-in cancelled.');
          } else if (nativeErr.message?.includes('network')) {
            setError('Network error. Check your connection.');
          } else if (nativeErr.message?.includes('DEVELOPER_ERROR')) {
            setError('Configuration error. Check your Google Services setup.');
          } else {
            setError(`Sign-in failed: ${nativeErr.message || 'Please check configuration.'}`);
          }
          setLoading(false);
        }
      } else {
        console.log('Starting web Google sign-in (redirect)...');
        
        const provider = new GoogleAuthProvider();
        
        try {
          const result = await signInWithPopup(auth, provider);
          console.log('Popup sign-in successful:', result.user.email);
        } catch (popupErr) {
          console.warn('Popup blocked or COOP issue, using redirect:', popupErr.code);
          await signInWithRedirect(auth, provider);
        }
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      
      if (err.code === 'auth/popup-closed-by-user' || err.message?.includes('cancelled')) {
        setError('Sign-in cancelled.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Pop-up blocked. Redirecting...');
        try {
          const provider = new GoogleAuthProvider();
          await signInWithRedirect(auth, provider);
        } catch (redirectErr) {
          console.error('Redirect also failed:', redirectErr);
          setError('Sign-in failed. Please try again.');
        }
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('Domain not authorized. Contact support.');
      } else if (err.message?.includes('network')) {
        setError('Network error. Check your connection.');
      } else {
        setError('Sign-in failed. Please try again.');
      }
      
      setLoading(false);
    }
  }, []);

  // ============================================
  // EMAIL AUTH
  // ============================================
  const handleEmailAuth = useCallback(async (e) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    // Basic validation first
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!trimmedPassword) {
      setError('Password is required.');
      return;
    }

    setLoading(true);
    setError('');

    if (!isLogin) {
      if (!isValidPassword(trimmedPassword)) {
        const errors = getPasswordErrors(trimmedPassword);
        setError(`Password must contain: ${errors.join(', ')}.`);
        setLoading(false);
        return;
      }

      const commonPasswords = ['password', '12345678', 'qwerty', 'abc123', 'password1', 'password123'];
      if (commonPasswords.includes(trimmedPassword.toLowerCase())) {
        setError('This password is too common. Please choose a stronger password.');
        setLoading(false);
        return;
      }
    }

    try {
      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
        console.log('User signed in:', userCredential.user.email);
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
        console.log('User created:', userCredential.user.email);
      }
    } catch (err) {
      console.error('Email auth error:', err);
      
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered. Please sign in or use a different email.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please use a stronger password.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address format.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-credential':
          setError('Invalid email or password.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later or reset your password.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your connection and try again.');
          break;
        default:
          setError('Authentication failed. Please try again.');
      }
      setLoading(false);
    }
  }, [email, password, isLogin]);

  // ============================================
  // PASSWORD RESET
  // ============================================
  const handlePasswordReset = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('Email is required.');
      setLoading(false);
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, trimmedEmail);
      setResetEmailSent(true);
      setTimeout(() => {
        setShowResetPassword(false);
        setResetEmailSent(false);
      }, 5000);
    } catch (err) {
      console.error('Password reset error:', err);
      
      switch (err.code) {
        case 'auth/user-not-found':
          setResetEmailSent(true);
          setTimeout(() => {
            setShowResetPassword(false);
            setResetEmailSent(false);
          }, 5000);
          break;
        case 'auth/invalid-email':
          setError('Invalid email address format.');
          break;
        case 'auth/too-many-requests':
          setError('Too many requests. Please try again later.');
          break;
        default:
          setError('Failed to send reset email. Please try again.');
      }
    }
    
    setLoading(false);
  }, [email]);

  const toggleAuthMode = useCallback(() => {
    setIsLogin(!isLogin);
    setEmail('');
    setPassword('');
    setError('');
    setShowResetPassword(false);
    setResetEmailSent(false);
  }, [isLogin]);

  const toggleResetPassword = useCallback(() => {
    setShowResetPassword(!showResetPassword);
    setError('');
    setResetEmailSent(false);
  }, [showResetPassword]);

  const handleSignOut = useCallback(() => {
    auth.signOut();
  }, []);

  const renderNavigationTabs = () => (
    <div style={{
      position: 'fixed',
      top: 'max(calc(env(safe-area-inset-top) + 12px), 12px)',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'calc(100% - 24px)',
      maxWidth: responsive.isTablet ? '450px' : '320px',
      background: 'rgba(255, 255, 255, 0.25)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '28px',
      padding: '4px',
      display: 'flex',
      gap: '4px',
      zIndex: 100,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        position: 'absolute',
        left: activeTab === 'login' ? '4px' : 'calc(50% + 2px)',
        top: '4px',
        width: `calc(50% - 4px)`,
        height: `calc(100% - 8px)`,
        background: 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '24px',
        transition: 'left 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        zIndex: 0,
      }} />

      <button
        onClick={() => setActiveTab('login')}
        style={{
          flex: 1,
          padding: '8px 12px',
          fontSize: responsive.isTablet ? '13px' : '12px',
          fontWeight: '600',
          border: 'none',
          borderRadius: '24px',
          background: 'transparent',
          color: activeTab === 'login' ? '#1f2937' : '#9ca3af',
          cursor: 'pointer',
          transition: 'color 0.3s ease',
          zIndex: 1,
          position: 'relative',
          fontFamily: 'inherit',
          userSelect: 'none'
        }}
      >
        Login
      </button>

      <button
        onClick={() => setActiveTab('about')}
        style={{
          flex: 1,
          padding: '8px 12px',
          fontSize: responsive.isTablet ? '13px' : '12px',
          fontWeight: '600',
          border: 'none',
          borderRadius: '24px',
          background: 'transparent',
          color: activeTab === 'about' ? '#1f2937' : '#9ca3af',
          cursor: 'pointer',
          transition: 'color 0.3s ease',
          zIndex: 1,
          position: 'relative',
          fontFamily: 'inherit',
          userSelect: 'none'
        }}
      >
        About Us
      </button>
    </div>
  );

  const renderLoginSection = () => (
    <>
      <div style={{ textAlign: 'center', marginBottom: responsive.isTablet ? '28px' : '22px' }}>
        <p style={{
          fontSize: responsive.isTablet ? '14px' : '13px',
          fontWeight: '500',
          color: '#238affff',
          margin: 0,
          letterSpacing: '0.4px',
          marginBottom: '4px'
        }}>
          Welcome to
        </p>
        <h1 style={{
          fontSize: responsive.isTablet ? '48px' : '28px',
          fontWeight: '400',
          fontFamily: '"Georgia", "Times New Roman", serif',
          color: '#000000ff',
          margin: 0,
          lineHeight: 1.1
        }}>
          Preptune
        </h1>
      </div>

      <div style={{
        width: '100%',
        overflow: 'visible',
        position: 'relative',
        height: responsive.isTablet ? '90px' : '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: responsive.isTablet ? '40px' : '32px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          willChange: 'transform',
        }}>
          {imageCards.map((card, idx) => {
            const isLoaded = loadedImages.includes(idx);
            return (
              <div 
                key={idx}
                style={{
                  width: responsive.isTablet ? '90px' : '64px',
                  height: responsive.isTablet ? '105px' : '75px',
                  flexShrink: 0,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: isLoaded ? 1 : 0,
                  filter: isLoaded ? 'blur(0px)' : 'blur(10px)',
                  transform: isLoaded 
                    ? `rotate(${card.rotation}deg) translateY(${Math.abs(card.rotation) * 1.5}px) scale(1)`
                    : `rotate(${card.rotation}deg) translateY(${Math.abs(card.rotation) * 1.5}px) scale(0.9)`,
                  willChange: 'transform, opacity, filter',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1.5px solid rgba(246, 251, 255, 0.77)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}>
                  <img 
                    src={card.image} 
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      userSelect: 'none',
                      WebkitUserSelect: 'none'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: responsive.isTablet ? '18px' : '14px' }}>
        <p style={{
          fontSize: responsive.isTablet ? '14px' : '13px',
          fontWeight: '500',
          color: '#374151',
          margin: 0,
          letterSpacing: '0.2px'
        }}>
          {isLogin ? 'Sign in to continue' : 'Create account'}
        </p>
      </div>

      <div style={{
        background: 'white',
        borderRadius: '28px',
        padding: responsive.isTablet ? '40px' : '20px',
        boxShadow: '0 2px 16px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(0, 0, 0, 0.04)',
        maxWidth: '100%'
      }}>
        {resetEmailSent ? (
          <div style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 0'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'rgba(34, 197, 94, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: '#22c55e'
            }}>✓</div>
            <div>
              <h3 style={{
                fontSize: responsive.isTablet ? '18px' : '16px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0,
                marginBottom: '4px'
              }}>Check your email</h3>
              <p style={{
                fontSize: responsive.isTablet ? '13px' : '12px',
                color: '#6b7280',
                margin: 0,
                lineHeight: 1.4
              }}>Reset link sent to {email}</p>
            </div>
          </div>
        ) : showResetPassword ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <h2 style={{
              fontSize: responsive.isTablet ? '20px' : '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: 0,
              textAlign: 'center'
            }}>Reset Password</h2>
            
            {error && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '10px 12px',
                borderRadius: '28px',
                fontSize: responsive.isTablet ? '13px' : '12px',
                textAlign: 'center',
                lineHeight: 1.4
              }}>{error}</div>
            )}

            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: responsive.isTablet ? '15px' : '14px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '28px',
                background: '#f9fafb',
                color: '#1f2937',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.background = 'white';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = '#f9fafb';
              }}
            />

            <button
              onClick={handlePasswordReset}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: responsive.isTablet ? '14px' : '13px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '28px',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#9ca3af' : '#000000',
                color: 'white',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                opacity: loading ? 0.7 : 1,
                fontFamily: 'inherit',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#1a1a1a')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#000000')}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              onClick={toggleResetPassword}
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 16px',
                fontSize: responsive.isTablet ? '14px' : '13px',
                fontWeight: '500',
                border: '1.5px solid #e5e7eb',
                borderRadius: '28px',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: 'transparent',
                color: '#374151',
                opacity: loading ? 0.6 : 1,
                fontFamily: 'inherit',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#f9fafb')}
              onMouseLeave={(e) => !loading && (e.target.style.background = 'transparent')}
            >
              Back
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {error && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '10px 12px',
                borderRadius: '16px',
                fontSize: responsive.isTablet ? '13px' : '12px',
                textAlign: 'center',
                lineHeight: 1.4
              }}>{error}</div>
            )}

            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: responsive.isTablet ? '15px' : '14px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '28px',
                background: '#f9fafb',
                color: '#1f2937',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.background = 'white';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = '#f9fafb';
              }}
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (!isLogin && e.target.value) {
                  setPasswordStrength(getPasswordStrength(e.target.value));
                } else {
                  setPasswordStrength(null);
                }
              }}
              disabled={loading}
              autoComplete={isLogin ? "current-password" : "new-password"}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: responsive.isTablet ? '15px' : '14px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '28px',
                background: '#f9fafb',
                color: '#1f2937',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.background = 'white';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = '#f9fafb';
              }}
            />

            {!isLogin && passwordStrength && password.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '-4px'
              }}>
                <div style={{
                  flex: 1,
                  height: '4px',
                  background: '#e5e7eb',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    background: passwordStrength.color,
                    width: passwordStrength.level === 'weak' ? '33%' : 
                           passwordStrength.level === 'medium' ? '66%' : '100%',
                    transition: 'all 0.3s ease',
                    borderRadius: '2px'
                  }} />
                </div>
                <span style={{
                  fontSize: responsive.isTablet ? '12px' : '11px',
                  fontWeight: '600',
                  color: passwordStrength.color,
                  textTransform: 'capitalize',
                  minWidth: '60px',
                  textAlign: 'right'
                }}>
                  {passwordStrength.level}
                </span>
              </div>
            )}

            <button
              onClick={handleEmailAuth}
              disabled={loading}
              style={{
                width: '100%',
                padding: responsive.isTablet ? '16px 18px' : '12px 16px',
                fontSize: responsive.isTablet ? '16px' : '14px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '28px',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#9ca3af' : '#000000',
                color: 'white',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                opacity: loading ? 0.7 : 1,
                fontFamily: 'inherit',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#1a1a1a')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#000000')}
            >
              {loading ? (isLogin ? 'Signing in...' : 'Creating...') : (isLogin ? 'Sign In' : 'Sign Up')}
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '4px 0'
            }}>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              <span style={{ fontSize: responsive.isTablet ? '14px' : '13px', color: '#9ca3af', fontWeight: '500' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                width: '100%',
                padding: responsive.isTablet ? '16px 18px' : '12px 14px',
                fontSize: responsive.isTablet ? '16px' : '14px',
                fontWeight: '600',
                border: '1.5px solid #e5e7eb',
                borderRadius: '28px',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: 'white',
                color: '#1f2937',
                opacity: loading ? 0.7 : 1,
                fontFamily: 'inherit',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#f9fafb')}
              onMouseLeave={(e) => !loading && (e.target.style.background = 'white')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              fontSize: responsive.isTablet ? '14px' : '13px',
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid #f0f0f0',
              flexWrap: 'wrap'
            }}>
              {isLogin ? (
                <>
                  <button
                    onClick={toggleResetPassword}
                    disabled={loading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#374151',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      padding: 0,
                      fontSize: responsive.isTablet ? '14px' : '13px',
                      opacity: loading ? 0.6 : 1,
                      fontFamily: 'inherit',
                    }}
                  >
                    Forgot?
                  </button>
                  <span style={{ color: '#d1d5db' }}>•</span>
                  <button
                    onClick={toggleAuthMode}
                    disabled={loading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      padding: 0,
                      fontSize: responsive.isTablet ? '14px' : '13px',
                      opacity: loading ? 0.6 : 1,
                      fontFamily: 'inherit',
                    }}
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <button
                  onClick={toggleAuthMode}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    padding: 0,
                    fontSize: responsive.isTablet ? '14px' : '13px',
                    opacity: loading ? 0.6 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  Have account? Sign In
                </button>
              )}
            </div>

            <p style={{
              fontSize: responsive.isTablet ? '14px' : '13px',
              color: '#6b7280',
              textAlign: 'center',
              lineHeight: 1.5,
              margin: '6px 0 0 0'
            }}>
              By continuing, you agree to{' '}
              <a href="https://preptune.com/terms/" style={{
                color: '#374151',
                textDecoration: 'none',
                fontWeight: '500'
              }}>
                Terms
              </a>
              {' '}and{' '}
              <a href="https://preptune.com/privacy/" style={{
                color: '#374151',
                textDecoration: 'none',
                fontWeight: '500'
              }}>
                Privacy
              </a>
            </p>
          </div>
        )}
      </div>
    </>
  );

  const renderAboutSection = () => (
    <>
      <div style={{ textAlign: 'center', marginBottom: responsive.isTablet ? '18px' : '12px' }}>
        <h1 style={{
          fontSize: responsive.isTablet ? '34px' : '26px',
          fontWeight: '400',
          fontFamily: '"Georgia", "Times New Roman", serif',
          color: '#000000ff',
          margin: 0,
          lineHeight: 1.2
        }}>
          Built for <span style={{ fontStyle: 'italic', fontWeight: '300', color: '#238affff' }}>you</span>
        </h1>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: responsive.isTablet ? '80px' : '60px',
        position: 'relative',
        marginBottom: responsive.isTablet ? '40px' : '30px'
      }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: responsive.isTablet ? '80px' : '66px',
              height: responsive.isTablet ? '100px' : '79px',
              borderRadius: '14px',
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1.5px solid rgba(246, 251, 255, 0.77)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              transform: `rotate(${0 + i * 12}deg) translateY(${i * 8}px) scale(${1 - i * 0.1})`,
              zIndex: 3 - i,
              opacity: 0.95 - i * 0.2,
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
          >
            <img 
              src={i === 0 ? "/image/intro22.png" : imageCards[i].image}
              alt="Preptune" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
            />
          </div>
        ))}
      </div>

      <div style={{
        background: 'white',
        borderRadius: '28px',
        padding: responsive.isTablet ? '28px' : '20px',
        boxShadow: '0 2px 16px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{
          fontSize: responsive.isTablet ? '13px' : '12px',
          color: '#4b5563',
          lineHeight: 1.6,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          marginBottom: '26px',
          paddingBottom: '16px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <p style={{ 
            margin: 0, 
            fontWeight: '600', 
            fontSize: responsive.isTablet ? '16px' : '15px', 
            color: '#1f2937',
            lineHeight: 1.5,
            textAlign:'left',
          }}>
            Personalized intelligence secured and designed to remember.
          </p>
          
          <p style={{ 
            margin: 0, 
            color: '#6b7280', 
            lineHeight: 1.5,
            fontSize: responsive.isTablet ? '14px' : '13px',
            textAlign:'left',
          }}>
            Chat naturally. Preptune adapts to your routine and keeps the conversation flowing every day.
          </p>
        </div>

        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: responsive.isTablet ? '16px' : '12px', 
          marginBottom: '16px',
          textAlign:'left',
        }}>
          {[
            {
              icon: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
              title: 'Remembers Conversations',
              desc: 'Builds on past chats naturally'
            },
            {
              icon: 'M3 11h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-11zM7 11V7a5 5 0 0 1 10 0v4',
              title: 'Encrypted & Private',
              desc: 'Journal and chat history only you access'
            },
            {
              icon: 'm10.827 5.465-.435-2.324m.435 2.324a5.338 5.338 0 0 1 6.033 4.333l.331 1.769c.44 2.345 2.383 2.588 2.6 3.761.11.586.22 1.171-.31 1.271l-12.7 2.377c-.529.099-.639-.488-.749-1.074C5.813 16.73 7.538 15.8 7.1 13.455c-.219-1.169.218 1.162-.33-1.769a5.338 5.338 0 0 1 4.058-6.221Zm-7.046 4.41c.143-1.877.822-3.461 2.086-4.856m2.646 13.633a3.472 3.472 0 0 0 6.728-.777l.09-.5-6.818 1.277Z',
              title: 'Complete Toolkit',
              desc: 'Journal, schedule, study, podcasts'
            },
            {
              icon: 'M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7v.01',
              title: 'Always Free',
              desc: 'No subscriptions or fees'
            }
          ].map((item, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{
                width: responsive.isTablet ? '40px' : '32px',
                height: responsive.isTablet ? '40px' : '32px',
                borderRadius: '8px',
                background: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                minWidth: responsive.isTablet ? '40px' : '32px',
                border: '1px solid #e5e7eb'
              }}>
                <svg width={responsive.isTablet ? '20' : '16'} height={responsive.isTablet ? '20' : '16'} viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={item.icon}/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ 
                  fontSize: responsive.isTablet ? '16px' : '14px', 
                  fontWeight: '600', 
                  color: '#1f2937', 
                  margin: 0, 
                  marginBottom: '2px'
                }}>
                  {item.title}
                </p>
                <p style={{ 
                  fontSize: responsive.isTablet ? '14px' : '13px', 
                  color: '#6b7280', 
                  margin: 0, 
                  lineHeight: 1.4,
                  marginBottom: responsive.isTablet ? '18px' : '15px'
                }}>
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <a href="/about-us" style={{
          display: 'block',
          width: '100%',
          padding: '12px 16px',
          background: '#000000',
          color: 'white',
          border: 'none',
          borderRadius: '28px',
          fontSize: responsive.isTablet ? '14px' : '13px',
          fontWeight: '600',
          cursor: 'pointer',
          textAlign: 'center',
          textDecoration: 'none',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          fontFamily: 'inherit'
        }}
        onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          Learn More
        </a>
      </div>
    </>
  );

  return (
    <div 
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(170deg, #ecf1ffff 0%, #fefefeff 60%, #e0e7fcff 100%)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '12px',
        paddingTop: authUser ? '120px' : 'max(calc(env(safe-area-inset-top) + 12px), 12px)',
        paddingBottom: 'max(calc(env(safe-area-inset-bottom) + 12px), 12px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
      }}>
      
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: '#000000',
        zIndex: 9998,
        height: 'env(safe-area-inset-top)'
      }} />

      {authUser && (
        <div style={{
          position: 'fixed',
          top: 'max(calc(env(safe-area-inset-top) + 8px), 8px)',
          left: '8px',
          right: '8px',
          background: '#10b981',
          color: 'white',
          padding: '12px',
          borderRadius: '12px',
          fontSize: responsive.isTablet ? '13px' : '12px',
          zIndex: 9999,
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
          textAlign: 'center',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{ fontWeight: 'bold', fontSize: responsive.isTablet ? '15px' : '14px', marginBottom: '4px' }}>
            SIGN-IN WORKING
          </div>
          <div style={{ fontSize: responsive.isTablet ? '12px' : '11px', opacity: 0.95 }}>
            {authUser.email}
          </div>
          <button
            onClick={handleSignOut}
            style={{
              marginTop: '6px',
              padding: '4px 12px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: responsive.isTablet ? '11px' : '10px',
              cursor: 'pointer',
              fontWeight: '500',
              fontFamily: 'inherit'
            }}
          >
            Sign Out
          </button>
        </div>
      )}

      {renderNavigationTabs()}

      <div style={{
        width: '100%',
        maxWidth: responsive.isTablet ? '700px' : '380px',
        display: 'flex',
        flexDirection: 'column',
        gap: responsive.isTablet ? '16px' : '12px',
        animation: 'appintroFadeIn 0.6s ease-out',
        paddingTop: responsive.isTablet ? '40px' : '56px',
        paddingBottom: responsive.isTablet ? '80px' : '8px',
        minHeight: responsive.isTablet ? '100vh' : 'auto',
        justifyContent: responsive.isTablet ? 'center' : 'flex-start'
      }}>
        {activeTab === 'login' ? renderLoginSection() : renderAboutSection()}
      </div>

      <style>{`
        @keyframes appintroFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        * {
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </div>
  );
};

export default AppIntro;